import { AN_OBJECT_IS_AT_MOST } from "@rally/agreed";
import type { Frame, Someone, Step } from "./working.ts";

export type Verb =
  | "thinking"
  | "reading"
  | "editing"
  | "running"
  | "searching"
  | "fetching"
  | "owed"
  | "typing";

const VERBS: ReadonlyArray<readonly [Verb, readonly string[]]> = [
  ["reading", ["read", "cat", "view", "open", "notebook_read", "read_file", "readfile"]],
  [
    "editing",
    ["write", "edit", "create", "apply_patch", "patch", "notebook_edit", "write_file", "multiedit"],
  ],
  ["running", ["bash", "sh", "zsh", "shell", "run", "exec", "execute", "command", "terminal"]],
  ["searching", ["grep", "glob", "search", "find", "rg", "ripgrep", "codebase_search"]],
  ["fetching", ["fetch", "web", "webfetch", "web_fetch", "http", "curl", "browse", "websearch"]],
];

export function verbOf(tool: string | null | undefined): Verb {
  const name = (tool ?? "").toLowerCase();
  if (!name) return "thinking";
  for (const [verb, tools] of VERBS) if (tools.includes(name)) return verb;
  return "running";
}

export function isKnownTool(tool: string | null | undefined): boolean {
  const name = (tool ?? "").toLowerCase();
  return VERBS.some(([, tools]) => tools.includes(name));
}

export type Doing = { verb: Verb; object: string | null };

export function doingOf(step: Step | null | undefined): Doing {
  if (!step) return { verb: "thinking", object: null };
  const verb = verbOf(step.tool);
  const known = isKnownTool(step.tool);
  const object = (step.object ?? "").trim() || null;
  if (!known) return { verb, object: object && object !== step.tool ? object : step.tool };
  return { verb, object: object === step.tool ? null : object };
}

export function lastStepOf(frame: Frame | null | undefined): Step | null {
  if (!frame || frame.did.length === 0) return null;
  return frame.did[frame.did.length - 1] ?? null;
}

export type Busy = {
  account: string;
  handle: string;
  displayName: string | null;

  role?: string | undefined;

  under?: string | null | undefined;

  run: string | null;
  startedAt: string | null;
  verb: Verb;
  object: string | null;

  why: string | null;
};

const RANK: Record<Verb, number> = {
  reading: 0,
  editing: 0,
  running: 0,
  searching: 0,
  fetching: 0,
  thinking: 0,
  owed: 1,
  typing: 2,
};

export function whoIsBusy(
  busy: { thinking: readonly Someone[]; unanswered: readonly Someone[]; typing: readonly Someone[] },
  stopped: ReadonlyMap<string, string> = new Map(),
): Busy[] {
  const rows: Busy[] = [];
  for (const who of busy.thinking) {
    const doing = doingOf(lastStepOf(who.frame));
    rows.push({
      ...bare(who),
      verb: doing.verb,
      object: doing.object,
      why: stopped.get(who.account) ?? null,
    });
  }

  for (const who of busy.unanswered) rows.push({ ...bare(who), verb: "owed", object: null, why: null });
  for (const who of busy.typing) {
    rows.push({ ...bare(who), run: null, startedAt: null, verb: "typing", object: null, why: null });
  }
  return rows.sort(
    (one, two) => RANK[one.verb] - RANK[two.verb] || began(one.startedAt) - began(two.startedAt),
  );
}

const bare = (who: Someone) => ({
  account: who.account,
  handle: who.handle,
  displayName: who.displayName,
  run: who.run ?? null,
  startedAt: who.startedAt ?? null,
});

function began(at: string | null): number {
  if (!at) return Number.MAX_SAFE_INTEGER;
  const when = new Date(at).getTime();
  return Number.isFinite(when) ? when : Number.MAX_SAFE_INTEGER;
}

export const MOST_ROWS = 10;

export function asATree(rows: readonly Busy[]): Array<Busy & { indented: boolean }> {
  const here = new Set(rows.map((row) => row.account));
  const leads = rows.filter((row) => !row.under || !here.has(row.under));
  const drawn: Array<Busy & { indented: boolean }> = [];
  for (const lead of leads) {
    drawn.push({ ...lead, indented: false });
    for (const row of rows) {
      if (row.under === lead.account && row.account !== lead.account) {
        drawn.push({ ...row, indented: true });
      }
    }
  }
  return drawn;
}

export function shownOf(rows: readonly Busy[]): { shown: Busy[]; hidden: number; total: number } {
  return {
    shown: rows.slice(0, MOST_ROWS),
    hidden: Math.max(0, rows.length - MOST_ROWS),
    total: rows.length,
  };
}

export function longestOf(rows: readonly Busy[]): string | null {
  let oldest: string | null = null;
  for (const row of rows) {
    if (!row.startedAt) continue;
    if (oldest === null || began(row.startedAt) < began(oldest)) oldest = row.startedAt;
  }
  return oldest;
}

export function runsOf(rows: readonly Busy[]): string[] {
  const runs: string[] = [];
  for (const row of rows) if (row.run && !row.why) runs.push(row.run);
  return runs;
}

export const A_TURN_IS_GIVEN = 10 * 60 * 1000;

export function quartersOf(
  startedAt: string | null,
  now: number,
  given = A_TURN_IS_GIVEN,
): 0 | 1 | 2 | 3 | 4 {
  if (!startedAt) return 0;
  const from = new Date(startedAt).getTime();
  if (!Number.isFinite(from)) return 0;
  const gone = now - from;

  if (gone < 0) return 0;
  const quarters = Math.floor((gone / given) * 4);
  return Math.max(0, Math.min(4, quarters)) as 0 | 1 | 2 | 3 | 4;
}

export function middleTruncated(said: string, most = AN_OBJECT_IS_AT_MOST): string {
  if (most < 5 || said.length <= most) return said;
  const tail = Math.max(1, Math.ceil((most - 1) * 0.7));
  const head = most - 1 - tail;
  return `${said.slice(0, head)}…${said.slice(said.length - tail)}`;
}
